import React from "react";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  msalInstance, loginMicrosoftPopup, getAccessTokenMicrosoft, apiTokenRequest
} from "./microsoftProvider";
import { gapiInstance } from "./googleProvider"
import User from "./User";
import { getUserRoles } from "API/UsersAPI";
import { getContact } from "API/ConsoleServicesAPI";
import UserLoginAPI from "API/UserLoginAPI";
import { fetchPhishingAPIKey } from "API/PhishingAPI";
import { SESSION_EXPIRED_EVENT } from './sessionExpiry';

export const AuthContext = React.createContext();
export const AuthConsumer = AuthContext.Consumer;

export class AuthProvider extends React.Component {

  // Authentication status
  NOT_AUTHENTICATED = "NOT_AUTHENTICATED";
  AUTHENTICATED = "AUTHENTICATED";
  LOADING = "LOADING";
  EMAIL_LOGIN = "EMAIL_LOGIN";
  EMAIL_REGISTER = "EMAIL_REGISTER";
  EMAIL_CONFIRM = "EMAIL_CONFIRM";
  FORGOT_PASSWORD = "FORGOT_PASSWORD";
  RESET_PASSWORD = "RESET_PASSWORD";

  constructor(props) {
    super(props);

    // restore the session before the first render, otherwise the app
    // redirects to the login page and the requested URL (deep link) is lost
    const user = User.getUserInfo();
    this.state = {
      siteKey: process.env.REACT_APP_GOOGLE_CAPTCHA_SITE_KEY,
      user: user || {},
      status: user ? this.AUTHENTICATED : this.NOT_AUTHENTICATED,
      error: ''
    };

    this.loginMicrosoft = this.loginMicrosoft.bind(this);
    this.loginGoogle = this.loginGoogle.bind(this);
    this.loginEmailPassword = this.loginEmailPassword.bind(this);
    this.registerEmailPassword = this.registerEmailPassword.bind(this);
    this.doLoginEmailPassword = this.doLoginEmailPassword.bind(this);
    this.confirmEmail = this.confirmEmail.bind(this);
    this.forgotPassword = this.forgotPassword.bind(this);
    this.resetPassword = this.resetPassword.bind(this);
    this.requestUserInfo = this.requestUserInfo.bind(this);
    this.onLoginFailure = this.onLoginFailure.bind(this);
    this.getLoginFailureMessage = this.getLoginFailureMessage.bind(this);
    this.doLogout = this.doLogout.bind(this);
    this.emailAuth = this.emailAuth.bind(this);
    this.emailAuthRoute = this.emailAuthRoute.bind(this);
    this.inProgress = this.inProgress.bind(this);
    this.authenticated = this.authenticated.bind(this);
    this.loginAsUser = this.loginAsUser.bind(this);
    this.onSessionExpired = this.onSessionExpired.bind(this);
  }

  componentDidMount() {
    window.addEventListener(SESSION_EXPIRED_EVENT, this.onSessionExpired);
  }

  componentWillUnmount() {
    window.removeEventListener(SESSION_EXPIRED_EVENT, this.onSessionExpired);
  }

  onSessionExpired() {
    if (this.state.status !== this.AUTHENTICATED || !User.getUserInfo()) return;
    sessionStorage.removeItem(VERITAS_TOKEN_KEY);
    User.clearUserInfo();
    const message = this.props.t('login.session_expired');
    this.setState({ user: {}, status: this.NOT_AUTHENTICATED, error: message });
    toast.error(message);
  }

  async loginMicrosoft() {
    // React state updates alone cannot guard two clicks in the same event loop.
    if (this.microsoftLoginPending) return;
    this.microsoftLoginPending = true;
    this.setState({
      status: this.LOADING
    });
    try {
      const result = await loginMicrosoftPopup();
      // console.debug('Login Microsoft result', result);
      const account = result.account;
      if (account) {
        await this.onLoginSuccess(account.name, account.username, account.tenantId, User.MICROSOFT);
      } else {
        throw new Error("Unable to get Microsoft Account information.");
      }
    } catch (error) {
      this.onLoginFailure(User.MICROSOFT, error);
    } finally {
      this.microsoftLoginPending = false;
    }
  }

  async loginGoogle() {
    this.setState({
      status: this.LOADING
    });
    try {
      const result = await gapiInstance.signIn();
      // console.debug('Login Google result', result);
      let profile = result.getBasicProfile();
      if (profile) {
        await this.onLoginSuccess(profile.getName(), profile.getEmail(), null, User.GOOGLE);
      } else {
        throw new Error("Unable to get Google Profile information.");
      }
    } catch (error) {
      this.onLoginFailure(User.GOOGLE, error);
    }
  }

  async loginAsUser(name, email) {
    try {
      const accessToken = await getAccessToken(User.VERITAS);
      const result = await UserLoginAPI.token(email, accessToken, User.VERITAS);
      if (result && result.cod === "200" && result.jwt_token) {
        sessionStorage.setItem(VERITAS_TOKEN_KEY, result.jwt_token);
      } else {
        throw Error(result);
      }
      await this.onLoginSuccess(name, email, null, User.VERITAS);
    } catch (error) {
      console.error("Unable to login as user.", error);
      throw new Error("Unable to login as user " + email);
    }
  }

  loginEmailPassword() {
    this.setState({
      status: this.EMAIL_LOGIN
    });
  }

  registerEmailPassword() {
    this.setState({
      status: this.EMAIL_REGISTER
    });
  }

  forgotPassword() {
    this.setState({
      status: this.FORGOT_PASSWORD
    });
  }

  doLoginEmailPassword(email, password) {
    email = email.trim().toLowerCase();
    this.setState({
      status: this.LOADING
    });
    UserLoginAPI.login(email, password).then(result => {
      const code = result && (result.cod || result.code);
      // current API returns jwt_token; older versions returned data.{name,email,token}
      const data = (result && result.data) || {};
      const token = (result && result.jwt_token) || data.token;
      if (String(code) === "200" && token) {
        // the token is needed to request the user roles during login
        sessionStorage.setItem(VERITAS_TOKEN_KEY, token);
        this.onLoginSuccess(data.name || email, data.email || email, null, User.VERITAS)
          .then(_ => {
            if (this.state.status !== this.AUTHENTICATED) {
              sessionStorage.removeItem(VERITAS_TOKEN_KEY);
            }
          });
      } else {
        console.error("Unexpected login response", result);
        throw new Error((result && (result.message || result.data)) || "Unable to get EmailVeritas Login information.");
      }
    }).catch(error => {
      this.onLoginFailure(User.VERITAS, error);
    });
  }

  confirmEmail() {
    this.setState({
      status: this.EMAIL_CONFIRM
    });
  }

  resetPassword() {
    this.setState({
      status: this.RESET_PASSWORD
    });
  }

  async onLoginSuccess(name, email, tenant, provider) {
    try {
      console.log(`Sucessful login using ${provider}.`);
      // Exchange token, if needed
      await this.exchangeToken(provider, email);
      // Store user without roles
      const user = new User(name, email, tenant, null, provider, null);
      User.setUserInfo(user);
      // Retrieve the user roles and the organization key
      const info = await this.requestUserInfo();
      user.roles = info.rules;
      // the API resolves the organization: the e-mail itself for free e-mail
      // providers (gmail.com, outlook.com, ...), otherwise the domain
      if (info.domain) {
        user.domain = info.domain;
      }
      try {
        const key = await fetchPhishingAPIKey(user.domain);
        user.phishingAPIKey = key.apikey;
      } catch (error) {
        // An unavailable optional simulator must not prevent console login.
        user.phishingAPIKey = null;
        console.warn('Phishing simulator is temporarily unavailable');
      }
      User.setUserInfo(user);
      this.setState({
        user: user,
        status: this.AUTHENTICATED,
        error: ''
      });
    } catch (error) {
      this.onLoginFailure(provider, error);
    }
  }

  async exchangeToken(provider, email) {
    try {
      switch (provider) {
        case User.MICROSOFT:
        case User.GOOGLE:
          const token = await getAccessToken(provider, email);
          const result = await UserLoginAPI.token(email, token, provider);
          const tokenValue = result?.jwt_token || result?.data?.token;
          if (String(result?.cod ?? result?.code) === "200" && tokenValue) {
            sessionStorage.setItem(VERITAS_TOKEN_KEY, tokenValue);
          } else {
            throw Error(result);
          }
          break;
        default:
          // console.warn("Unable to exchange token for provider", user.provider);
          break;
      }
    } catch (error) {
      console.error("Unable to exchange token.", error);
      throw new Error("Unable to exchange token for provider " + provider);
    }
  }

  async requestUserInfo() {
    try {
      const result = await getUserRoles();
      if (!result || !result.rules || result.rules.length <= 0) {
        throw new Error('NO_RULES');
      }
      return result;
    } catch (error) {
      console.error("Error retriveing user info.", error);
      const contact = await getContact();
      if (error.code === 401) {
        throw new Error(this.props.t('login.unauthorized', { contact: contact.email }));
      } else if (error.message === 'NO_RULES') {
        throw new Error(this.props.t('login.misconfigured', { contact: contact.email }));
      }
      throw new Error(this.props.t('login.error'));
    }
  }

  onLoginFailure(provider, error) {
    console.error(`Error on login using ${provider}.`, error);
    const message = this.getLoginFailureMessage(provider, error);
    this.setState({
      user: '',
      status: this.NOT_AUTHENTICATED,
      error: message
    });
    sessionStorage.removeItem(VERITAS_TOKEN_KEY);
    User.clearUserInfo();
    // show it once here; showing it on render repeats it on every re-render
    if (message) {
      toast["error"](message);
    }
  }

  getLoginFailureMessage(provider, error) {
    switch (provider) {
      case "Microsoft":
        if (error?.errorCode === 'interaction_in_progress') return this.props.t('login.microsoft_in_progress');
        return error?.errorMessage || error?.message || this.props.t('login.error');
      case "Google":
        return gapiInstance.errorMessage(error);
      default:
        return error.message;
    }
  }

  doLogout() {
    const user = this.state.user;
    const provider = user ? user.provider : null;
    switch (provider) {
      case User.MICROSOFT:
        const logoutRequest = {
          account: msalInstance.getAccountByUsername(user.email)
        };
        msalInstance.logoutRedirect(logoutRequest);
        break;
      case User.GOOGLE:
        gapiInstance.signOut().catch(error => console.warn('Unable to sign out of Google.', error));
        break;
      case User.VERITAS:
        sessionStorage.removeItem(VERITAS_TOKEN_KEY);
        break;
      default:
        console.warn("Unable to logout provider:", provider);
        break;
    }
    this.setState({
      user: '',
      status: this.NOT_AUTHENTICATED,
      error: ''
    });
    User.clearUserInfo();
  }

  emailAuth() {
    return this.state.status === this.EMAIL_LOGIN
      || this.state.status === this.EMAIL_REGISTER
      || this.state.status === this.EMAIL_CONFIRM
      || this.state.status === this.FORGOT_PASSWORD
      || this.state.status === this.RESET_PASSWORD;
  }

  emailAuthRoute() {
    switch (this.state.status) {
      case this.EMAIL_REGISTER:
        return "register";
      case this.FORGOT_PASSWORD:
        return "password";
      case this.EMAIL_CONFIRM:
        return "confirm-email";
      case this.RESET_PASSWORD:
        return "reset-password";
      case this.EMAIL_LOGIN:
      default:
        return "login";
    }
  }

  inProgress() {
    return this.state.status === this.LOADING;
  }

  authenticated() {
    return this.state.status === this.AUTHENTICATED;
  }

  render() {
    return (
      <AuthContext.Provider value={
        {
          loginMicrosoft: this.loginMicrosoft,
          loginGoogle: this.loginGoogle,
          loginAsUser: this.loginAsUser,
          loginEmailPassword: this.loginEmailPassword,
          registerEmailPassword: this.registerEmailPassword,
          forgotPassword: this.forgotPassword,
          confirmEmail: this.confirmEmail,
          resetPassword: this.resetPassword,
          doLoginEmailPassword: this.doLoginEmailPassword,
          doLogout: this.doLogout,
          emailAuth: this.emailAuth,
          emailAuthRoute: this.emailAuthRoute,
          inProgress: this.inProgress,
          authenticated: this.authenticated,
          siteKey: this.state.siteKey,
          user: this.state.user,
          error: this.state.error
        }
      }>
        {this.props.children}
      </AuthContext.Provider>
    )
  }
}

export default withTranslation()(AuthProvider)

// EmailVeritas session token key
const VERITAS_TOKEN_KEY = "eVeritasToken"

/**
 * Get the access token based on the authentication provider.
 * @return array composed of provider and token or null if token not found.
 */
export async function getAccessToken(provider, email) {
  let token = null;
  switch (provider) {
    case User.MICROSOFT:
      token = await getAccessTokenMicrosoft(email, apiTokenRequest);
      break;
    case User.GOOGLE:
      // Use ID Token for authentication validation 
      token = await gapiInstance.getIdToken();
      break;
    case User.VERITAS:
      token = sessionStorage.getItem(VERITAS_TOKEN_KEY);
      break;
    default:
      console.warn("Invalid Provider: ", provider);
      break;
  }
  return token;
}
